import React, { useEffect, useRef } from "react";
import axios from "axios";
import Hls from "hls.js";
import { getQueryVariable } from "Helpers/QueryString";
import CheckBandwidth from "../CheckBandwidth";
import { languages } from "../../Helpers/languages";
import Captions from "../Layout/Captions";
import { BASE_PATH } from "constants/awsBaseUrl";

import "./Video.css"

const Video = ({
  video,
  embed,
  pub_user,
  type,
  url,
  viewerId,
  clearUpdate,
  update,
  shouldUpdate,
  scrubTo,
  file,
  translations,
  setViews,
  setItemBeingBought,
  register,
}) => {
  let videoplayer = useRef(null);
  let mounted = useRef(null);
  // We don't want react to update when this changes, so that is why it is not in state
  let bufferSize = 0;

  const [state, setState] = React.useState({
    file: video,
    url: "",
    updating: false,
    price: 0,
    shouldPlay: embed ? true : true,
    videoMP4URL: "",
    videoPaidFor: false,
    user: pub_user ? pub_user._id : "", // this is the viewer in the DB
  });
  const [customThumb, setCustomThumb] = React.useState(false)
  const maxLoadedFrag = useRef(-1)
  // default attempt is to get video in HLS format; that is why type is initialized to HLS
  const getVideo = async (type = "HLS") => {
    if (mounted) {
      await axios
        .post(url + "/upload/get", {
          file: video,
          type,
          user: pub_user ? pub_user._id : "",
        })
        .then(async (res) => {
          if (res.data.response) {
            if (res.data.response.payload.data === "/") {
              /* 
              fetch details if a pay-to-view video has been bought.
              along with the url to the mp4 version of that video.
              Only run if a user has been logged in
            */
              if (viewerId) {
                //If no url is returned in the response retrieve mp4 version from the stripe api
                await axios
                  .post(
                    "https://do2v45e3qi.execute-api.us-west-2.amazonaws.com/production/get-video-from-aws",
                    {
                      videoId: state.file,
                      viewerId: viewerId,
                    }
                  )
                  .then((res) => {
                    setState((prev) => {
                      return {
                        ...prev,
                        videoMP4URL: res.data.videoMP4.split("?")[0],
                        videoPaidFor: res.data.videoHasBeenPaidFor,
                      };
                    });
                  });
              }
              setState((prev) => {
                return {
                  ...prev,
                  shouldPlay: false,
                  price: res.data.response.payload.price,
                };
              });
            } else {
              setState((prev) => {
                return {
                  ...prev,
                  url: res.data.response.payload.data.split("?")[0],
                  price: 0,
                  shouldPlay: embed ? false : true,
                };
              });

              if (
                res.data.response.payload.data
                  .split("/")
                  [res.data.response.payload.data.split("/").length - 1].split(
                    "?"
                  )[0]
                  .split(".")[1] === "mp4"
              ) {
                createmp4(res.data.response.payload.data);
              } else {
                createhls(res.data.response.payload.data);
              }
            }
          }
        })
        .catch((err) => {
          console.error(err);
          if (type === "HLS") {
            console.warn("HLS Failed. Trying MP4...");
            getVideo("MP4");
          }
        });
    }
  };

  const start = () => {
    if (mounted) {
      setTimeout(() => {
        start();
      }, 1000);
    }
    try {
      if (state.shouldPlay)
        update(document.getElementById("video").currentTime);
    } catch (e) {
      mounted = false;
    }
  };

  useEffect(() => {
    (async () => {
      if (!state.updating && shouldUpdate) {
        setState((prev) => {
          return { ...prev, updating: true };
        });
        document.getElementById("video").currentTime = scrubTo;
        clearUpdate();
        setState((prev) => {
          return { ...prev, updating: false };
        });
      }
      if (state.file !== video || (pub_user && state.user !== pub_user._id)) {
        setState((prev) => {
          return {
            ...prev,
            file: video,
            user: pub_user._id,
          };
        });
        await getUrl();
      }
      if (file.metadata) {
        insAndOuts(file.metadata);
      }
    })();
  });

  useEffect(() => {
    // check if the video is a live stream then fetch the live stream from aws
    (async () => {
      setState((prev) => {
        return { ...prev, file: video };
      });
      axios.get(`${BASE_PATH}/outputs/${encodeURIComponent(state.file)}/Default/Thumbnails/out_003.png?refresh=${new Date().getMonth()}${new Date().getDate()}${new Date().getFullYear()}`).then(res=>{if(res.status==200){setCustomThumb(true)}})

      await getUrl();

      mounted = true;
      start();
    })();

    return () => {
      updateViews(true);
      mounted = false;
    };
  }, []);

  const getUrl = async () => {
    if (type === "live" && mounted) {
      await axios.post(`${url}/upload/getLive`, { live: video }).then((res) => {
        if (res.data.response.success) {
          let { live } = res.data.response.payload;
          live = live.trim();
          setState((prev) => {
            return { ...prev, url: live };
          });
          createhls(live);
        }
      });
    } else {
      getVideo();
    }
  };

  const createmp4 = (url) => {
    let video = videoplayer.current;
    video.src = url;
    document.getElementById("video").currentTime = getQueryVariable("start");
    let playPromise = video.play();
    // Handling rejected promise from video.play to prevent the DOMException that comes from an unhandled play promise
    playPromise.catch((err) => {
      if (err.name === "AbortError") {
        // This is expected if playback was aborted because the video player unmounted in between play being called and resolving,
        // so no need to log in that case.
        if (mounted) {
          console.error(`Video Playback aborted: ${err.message}`);
        }
      } else {
        console.error(`Error playing video: ${err}`);
      }
    });
  };

  const createhls = (url) => {
    if (Hls.isSupported()) {
      let video = videoplayer.current;
      bufferSize = 0;
      let hls = new Hls({ autoStartLoad: true });
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(url);
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (!embed) {
          document.getElementById("video").currentTime =
            getQueryVariable("start");
          let playPromise = video.play();
          // Handling rejected promise from video.play to prevent the DOMException that comes from an unhandled play promise
          playPromise.catch((err) => {
            if (err.name === "AbortError") {
              // This is expected if playback was aborted because the video player unmounted in between play being called and resolving,
              // so no need to log in that case.
              if (mounted) {
                console.error(`HLS Playback aborted: ${err.message}`);
              }
            } else {
              console.error(`Error playing hls: ${err}`);
            }
          });
        }
      });
      hls.on(Hls.Events.BUFFER_APPENDING, (e, data) => {
        if (data && data.data) {
          bufferSize += data.data.byteLength;
        }
      });
      // If the m3u8 goes back to fragment 0, that means that the ip output was probably restarted
      // When this happens, we need to restart Hls.js in order to continue playback correctly.
      hls.on(Hls.Events.FRAG_LOADING, function (event, data) {
        if(type === "live") {
          let {frag} = data
          if(frag.sn > maxLoadedFrag.current) {
            maxLoadedFrag.current = frag.sn
          } else if(frag.sn === 0) {
            // RESET
            maxLoadedFrag.current = -1
            hls.destroy();
            getUrl();
          }
        }
      })
      hls.on(Hls.Events.ERROR, function (event, data) {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('fatal media error encountered, try to recover');
              hls.recoverMediaError();
              break;
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('fatal network error encountered', data);
              // All retries and media options have been exhausted.
              // Immediately trying to restart loading could cause loop loading.
              // Consider modifying loading policies to best fit your asset and network
              // conditions (manifestLoadPolicy, playlistLoadPolicy, fragLoadPolicy).
              break;
            default:
              // cannot recover
              hls.destroy();
              // recreate if live
              if(type === "live") {
                getUrl()
              }
              break;
          }
        }
      });
      hls = hls;
    }
  };

  const updateViews = (force = false) => {
    let viewData = {};
    if (bufferSize) {
      viewData.byteLength = bufferSize;
    }
    if (videoplayer.current) {
      if (videoplayer.current.videoWidth && videoplayer.current.videoHeight) {
        viewData.vWidth = videoplayer.current.videoWidth;
        viewData.vHeight = videoplayer.current.videoHeight;
      }
      if (videoplayer.current.played) {
        let ranges = videoplayer.current.played;
        let arr = [];
        for (let i = 0; i < ranges.length; i++) {
          arr.push({
            start: ranges.start(i),
            end: ranges.end(i),
          });
        }
        if (arr.length) {
          viewData.played = arr;
        }
      }
    }
    setViews(force, viewData, type);
  };

  const insAndOuts = (metadata) => {
    if (metadata?.in || metadata?.In || metadata?.out || metadata?.Out) {
      let ins = Number(metadata.in ? metadata.in : metadata.In);
      let outs = Number(metadata.out ? metadata.out : metadata.Out);
      var time = document.getElementById("video");
  
      if (time.currentTime < ins) {
        time.currentTime = ins;
      } else if (time.currentTime > outs) {
        time.currentTime = outs;
        if (mounted && videoplayer.current) {
          videoplayer.current.pause();
        }
      };
    };
  };
  let customThumbnailUrl=`${BASE_PATH}/outputs/${encodeURIComponent(state.file)}/Default/Thumbnails/out_003.png?refresh=${new Date().getMonth()}${new Date().getDate()}${new Date().getFullYear()}`
  let og=`${BASE_PATH}/outputs/${encodeURIComponent(state.file)}/Default/Thumbnails/out_003.png?refresh=${new Date().getMonth()}${new Date().getDate()}${new Date().getFullYear()}`
  return (
    <div className={`videoPlayerContainer ${embed?"embedded":""}`} ref={mounted}>
      {!state.videoPaidFor && (
        <div
          className="video-thumbnail"
          style={{
            backgroundImage: customThumb?`url('${customThumbnailUrl}')`:`url('${og}')`,
            display: state.shouldPlay ? "none" : "flex",
          }}
        >
          <div>
            <i
              className={
                "fas " + (state.price > 0 ? "fa-dollar-sign" : "fa-play-circle")
              }
              onClick={() => {
                setItemBeingBought("payToView");
                if (state.price > 0) {
                  // prompt user to make an account
                  if (pub_user._id === "") {
                    register();
                  } else {
                    setState((prev) => {
                      return { ...prev, checkout: true };
                    });
                  }
                } else {
                  let video = videoplayer.current;
                  video.play();
                  setState((prev) => {
                    return {
                      ...prev,
                      shouldPlay: true,
                    };
                  });
                }
              }}
            ></i>
          </div>
        </div>
      )}

      <video
        width="100%"
        controls
        ref={videoplayer}
        id="video"
        className={embed?"embeddedVideo":""}
        crossOrigin="anonymous"
        poster={customThumb?customThumbnailUrl:og}
        src={state.url ? state.url : state.videoMP4URL}
        onTimeUpdate={() => updateViews()}
        onPause={() => updateViews(true)}
        onPlay={() => updateViews(true)}
      >
        <source src={state.url} type="application/x-mpegURL" />
        <Captions
          translations={translations}
          type={type}
          video={video}
          languages={languages}
        />
      </video>
    </div>
  );
};
export default CheckBandwidth(Video);
